import {
  Box,
  Container,
  Fade,
  IconButton,
  makeStyles
} from '@material-ui/core';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useLanguage } from '../../../../context/language/language';
import useResponsive from '../../../../utils/responsive';
import { PlacesSearch } from '../../../common';
import useCustomerAddressMethod, {
  ADDRESS_EDIT,
  ADDRESS_GET,
} from '../../hook/useCutomerAddressMethod';
import useDeliveryMethod from '../../hook/useDeliveryMethod';
import Layout from '../Layout';
import ComponentAddressDetail from './ComponentAddressDetail';
import ComponentCustomerAllAddress from './ComponentCustomerAllAddress';
import useDeliveryData from '../../../common/hook/useDeliveryData';
import useDeliveryControlFlow from '../../../common/hook/useDeliveryControlFlow';
import useCustomerAddressList from '../../../common/hook/useCustomerAddressList';
import {
  CUSTOMER_ADDRESS_ALL,
  CUSTOMER_EDIT_ADDRESS,
  CUSTOMER_ADDRESS,
  DELIVERY_ADDRESS,
  SELECT_STORE
} from '../../../../constant/deliveryFlow';
import { defaultModalSize, mobileContentContainerHeight } from '../modalSize';
import { getImageURL } from '../../../../utils/getImageURL';
import { trackPageLoad } from '../../../../utils/adobeAnalytics';
import { interpretAddress } from '../../../../utils/mapAddressUtils';

const IconLocation = getImageURL('/icon/currentlocation.svg');
const IconMap = getImageURL('/icon/map.svg');
const IconEdit = getImageURL('/icon/edit.svg');

const useStyle = makeStyles((theme) => ({
  root: {
    padding: theme.isTablet ? theme.spacing(2) : 0,
  }
}));

const Main = styled.div(({ theme, width }) => ({
  width,
  minHeight: 'auto',
  // height: theme.isTabletOrMobile ? '75vh' : 'auto',
  overflow: 'auto',
  '&::-webkit-scrollbar-track': {
    borderRadius: 4,
    backgroundColor: '#FFFFFF',
  },
  '&::-webkit-scrollbar': {
    width: 4,
    backgroundColor: '#FFFFFF',
  },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: 4,
    backgroundColor: '#E8E8E8',
  },
}));

const Title = styled.div(() => ({
  overflow: 'hidden',
  width: '100%',
  display: '-webkit-box',
  WebkitLineClamp: 1,
  WebkitBoxOrient: 'vertical',
  textOverflow: 'ellipsis',
}));
const SubTitle = styled.div(() => ({
  overflow: 'hidden',
  width: '100%',
  display: '-webkit-box',
  WebkitLineClamp: 1,
  WebkitBoxOrient: 'vertical',
  textOverflow: 'ellipsis',
}));

const Icon = styled.img(({ w, h }) => ({
  width: w,
  height: h,
}));

const BtnIcon = styled(IconButton)(({
  w = 24, h = 24, ml, mb, mr, flexGrow
}) => ({
  width: w,
  height: h,
  marginLeft: ml,
  marginBottom: mb,
  marginRight: mr,
  flexGrow,
}));

const LinkAll = styled.div(({ theme, flexGrow }) => ({
  ...theme.fonts.textS,
  textDecoration: 'none',
  color: theme.colors.green600,
  cursor: 'pointer',
  flexGrow,
}));

const Label = styled.div(({
  theme, ml, mr, flexGrow,
}) => ({
  ...theme.fonts.header5,
  marginLeft: ml || 0,
  marginRight: mr || 0,
  flexGrow,
  color: theme.colors.grey700,
}));

const SubLabel = styled.div(({ theme }) => ({
  ...theme.fonts.textS,
  color: theme.colors.grey550,
  flexGrow: 1,
  overflow: 'hidden',
  width: '100%',
  display: '-webkit-box',
  WebkitLineClamp: theme.isTabletOrMobile ? 2 : 1,
  WebkitBoxOrient: 'vertical',
  textOverflow: 'ellipsis',
}));

const SubLabelPointer = styled(SubLabel)(({ theme }) => ({
  cursor: 'pointer',
}));

const LabelLastAddress = styled.div(({ theme }) => ({
  ...theme.fonts.header5,
  color: theme.colors.grey700,
}));

const SessionCurrentLocation = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  padding: theme.isTabletOrMobile ? '16px 0' : '16px 0 0 0',
  borderBottom: theme.isTabletOrMobile ? '0.5px solid #E0E0E0' : 'none',
}));

const SessionCurrentLocationDetail = styled.div(({ theme }) => ({
  flexDirection: 'column',
  flex: '1 1 auto',
  padding: '0 10px',
  overflow: 'hidden'
}));

const SessionCustomerAddress = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  margin: '16px 0 8px',
}));

const SessionCustomerLastAddress = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
}));

const SessionCustomerLastAddressItem = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
}));

const FlexBox = styled.div(({ width, marginTop }) => ({
  display: 'flex',
  flexDirection: 'column',
  width,
  marginTop,
}));

const LastAddress = ({ lastAddress, onClick, word }) => (
  <>
    <FlexBox marginTop="10px">
      <SessionCustomerLastAddress>
        <LabelLastAddress>{word('DeliveryAddress.RecentAddress')}</LabelLastAddress>
      </SessionCustomerLastAddress>
      <SessionCustomerLastAddressItem id="last-address" onClick={onClick}>
        <ComponentAddressDetail {...lastAddress} isLastUpdate />
      </SessionCustomerLastAddressItem>
    </FlexBox>
  </>
);

const CurrentLocationDetail = ({
  isAllowLocation, permissionState, currentAddress, handleSelectCurrentLocation, handleSelectMap
}) => {
  const word = useLanguage();
  if (isAllowLocation) {
    return (
      <SessionCurrentLocationDetail>
        <Label flexGrow={1}>{word('DeliveryAddress.CurrentLocation')}</Label>
        <SubLabelPointer flexGrow={1} onClick={handleSelectCurrentLocation}>{currentAddress}</SubLabelPointer>
      </SessionCurrentLocationDetail>
    );
  }
  if (permissionState === 'prompt') {
    return (
      <SessionCurrentLocationDetail>
        <Label flexGrow={1}>{word('DeliveryAddress.CurrentLocation.Off')}</Label>
        <SubLabelPointer flexGrow={1} onClick={handleSelectMap}>{word('DeliveryAddress.CurrentLocation.WebTurnOn')}</SubLabelPointer>
      </SessionCurrentLocationDetail>
    );
  } 
  return (
    <SessionCurrentLocationDetail>
      <Label flexGrow={1}>{word('DeliveryAddress.CurrentLocation.Web.Denied')}</Label>
      <SubLabelPointer flexGrow={1} onClick={handleSelectMap}>{word('DeliveryAddress.CurrentLocation.Web.Denied.Setting')}</SubLabelPointer>
    </SessionCurrentLocationDetail>
  );
};

export default function CustomerSelectAddress({
  onNext,
  onBack,
  onClose,
  modalRef,
}) {
  const customerAddress = useCustomerAddressList();
  const deliveryData = useDeliveryData();
  const deliveryControl = useDeliveryControlFlow();
  const { reset, setDataAddress } = useCustomerAddressMethod();
  const { isMobile, isTablet, isTabletOrMobile } = useResponsive();
  const word = useLanguage();
  const {
    recentAddress,
    getRecentAddress,
  } = useDeliveryMethod();
  const classes = useStyle();

  const addresses = _.take(customerAddress.addressList, 3);
  const [isAllowLocation, setIsAllowLocation] = useState(false);
  const [currentAddress, setCurrentAddress] = useState(null);
  const [currentCompleteAddress, setCurrentCompleteAddress] = useState(null);
  const [permissionState, setPermissionState] = useState(null);
  const [showToolTip, setShowToolTip] = useState(false);
  const [values, setValues] = useState({
    search: '',
    searchResult: [],
    isFilter: false,
  });
  const [disableScrollY, setDisableScrollY] = useState(false);

  useEffect(() => {
    getRecentAddress();
    customerAddress.getAddress();
    if (customerAddress.addressList?.length !== 0) {
      trackPageLoad('Delivery Address');
    }
  }, []);

  useEffect(() => {
    if (customerAddress.addressList?.length === 0 && customerAddress.dataCompleted) {
      deliveryControl.replacePage(DELIVERY_ADDRESS);
    }
  }, [customerAddress.addressList]);

  const handleSearchPlace = (address) => {
    setValues({ ...values, search: address });
    setShowToolTip(Boolean(address));
  };

  const handleSearchError = (status) => {
    setDisableScrollY(Boolean(status));
  };

  const handleViewAll = () => {
    onNext({ data: '', page: CUSTOMER_ADDRESS_ALL });
  };

  const handleAddAddress = () => {
    reset();
    setDataAddress({ customerId: customerAddress.customerId });
    onNext({ data: '', page: CUSTOMER_EDIT_ADDRESS });
  };

  const handleEditAddress = (address) => {
    setDataAddress({
      action: ADDRESS_EDIT,
      customerId: customerAddress.customerId,
      addressId: address.addressId,
      addressDetail: address,
    });
    onNext({ data: address, page: CUSTOMER_EDIT_ADDRESS });
  };

  const handleSelectAddress = (address) => {
    setDataAddress({
      action: ADDRESS_GET,
      customerId: customerAddress.customerId,
      addressId: address.addressId,
      addressDetail: address,
    });
    onNext({ data: address, page: SELECT_STORE });
  };

  const handleSelectPlace = (address, latLng) => {
    setValues({ ...values, search: address });
    setShowToolTip(false);
    onNext({
      data: {
        latitude: latLng.lat,
        longitude: latLng.lng,
        addressId: '',
        contactNo: '',
        deliveryNote: '',
        addressName: '',
        recipientName: '',
      },
      page: DELIVERY_ADDRESS
    });
  };

  const handleSelectMap = () => {
    onNext({
      data: {
        latitude: '',
        longitude: '',
        addressId: '',
        contactNo: '',
        deliveryNote: '',
        addressName: '',
        recipientName: '',
      },
      page: DELIVERY_ADDRESS
    });
  };

  const handleSelectCurrentLocation = () => {
    onNext({
      data: {
        latitude: currentCompleteAddress.latitude,
        longitude: currentCompleteAddress.longitude,
        addressId: '',
        contactNo: '',
        deliveryNote: '',
        addressName: '',
        recipientName: '',
      },
      page: DELIVERY_ADDRESS
    });
  };

  const onSelectRecent = () => {
    onNext({
      data: {
        ...recentAddress,
        addressId: '',
        contactNo: '',
        deliveryNote: '',
        addressName: '',
        recipientName: '',
      },
      page: SELECT_STORE
    });
  };

  const getCurrentLocation = () => {
    deliveryControl.setLoading(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({
          location: {
            lat: parseFloat(coords.latitude),
            lng: parseFloat(coords.longitude)
          }
        })
          .then((response) => {
            const [address, completedAddress] = interpretAddress(coords.latitude, coords.longitude, response.results[0]);
            setCurrentAddress(address);
            setCurrentCompleteAddress(completedAddress);
            setIsAllowLocation(true);
            deliveryControl.setLoading(false);
          })
          .catch((error) => {
            console.error(error);
            setIsAllowLocation(false);
            deliveryControl.setLoading(false);
          });
      }, (error) => {
        console.error(error);
        setIsAllowLocation(false);
        deliveryControl.setLoading(false);
      }
    );
  };

  const contentContainerRef = useRef(null);
  useEffect(() => {
    mobileContentContainerHeight(contentContainerRef, isMobile, modalRef);
  }, [contentContainerRef]);

  useEffect(() => {
    navigator.permissions.query({ name: 'geolocation' }).then((result) => {
      setPermissionState(result.state);
      if (result.state === 'granted') {
        getCurrentLocation();
      } else {
        setIsAllowLocation(false);
      }
    });
  }, []);

  return (
    <Layout
      title={(
        <Title>
          {word('DeliveryAddress.Title')}
        </Title>
      )}
      description={<SubTitle>{word('DeliveryAddress.Description')}</SubTitle>}
      onBack={onBack}
      backButtonShown={deliveryControl.stackSize > 1}
      onClose={onClose}
      width={isTablet ? defaultModalSize.tablet.width : defaultModalSize.desktop.width}
      height={isTablet ? defaultModalSize.tablet.height : defaultModalSize.desktop.height}
      scrollY={!disableScrollY}
    >
      <Container className={classes.root} ref={contentContainerRef}>
        <Main>
          <PlacesSearch
            placeholder={word('DeliveryAddress.Search')}
            placeholderIsMobile={word('DeliveryAddress.Search')}
            notShowTooltip
            name="search"
            value={values.search}
            onSearch={handleSearchPlace}
            onSelect={handleSelectPlace}
            onClose={() => setShowToolTip(false)}
            onError={handleSearchError}
            size="small"
            IconBtn={(
              <BtnIcon w={24} h={24} id="map-icon" onClick={handleSelectMap}>
                <Icon w="18px !important" h={18} src={IconMap} />
              </BtnIcon>
            )}
            floating
          />
          {/* <!-- not search--> */}
          <Fade in={!showToolTip}>
            <FlexBox>
              {!showToolTip && (
                <>
                  <SessionCurrentLocation>
                    <BtnIcon
                      id="current-location-icon"
                      onClick={isAllowLocation ? handleSelectCurrentLocation : handleSelectMap}
                      mb={20}
                      ml={-5}
                    >
                      <Icon w="18px !important" h={22} src={IconLocation} />
                    </BtnIcon>
                    <CurrentLocationDetail
                      isAllowLocation={isAllowLocation}
                      permissionState={permissionState}
                      currentAddress={currentAddress}
                      handleSelectCurrentLocation={handleSelectCurrentLocation}
                      handleSelectMap={handleSelectMap}
                    />
                    <BtnIcon mr={isTabletOrMobile ? 4 : 22}>
                      <Icon onClick={isAllowLocation ? handleSelectCurrentLocation : handleSelectMap} w={16} h={16} src={IconEdit} />
                    </BtnIcon>
                  </SessionCurrentLocation>
                  {
                    !_.isEmpty(addresses) && (
                    <SessionCustomerAddress>
                      <Label flexGrow={1}>{word('DeliveryAddress.SavedAddress')}</Label>
                      { customerAddress.addressList.length > 3 && (
                      <LinkAll flexGrow={0} onClick={handleViewAll}>
                        {word('General.SeeAll')}
                      </LinkAll>
                      )}
                    </SessionCustomerAddress>
                    )
                  }
                  <ComponentCustomerAllAddress
                    selectedId={deliveryData.addressId}
                    maxLength={3}
                    total={customerAddress.addressList?.length}
                    addressList={addresses}
                    onAdd={handleAddAddress}
                    onEdit={handleEditAddress}
                    onSelect={handleSelectAddress}
                  />
                  {recentAddress && !_.isEmpty(recentAddress) && (
                    <LastAddress
                      word={word}
                      lastAddress={recentAddress}
                      onClick={onSelectRecent}
                    />
                  )}
                </>
              )}
            </FlexBox>
          </Fade>
        </Main>
      </Container>
    </Layout>
  );
}
